import { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';

// You'll need to create these components
import GolfIcon from '@/components/GolfIcon';
import ChatSkeleton from '@/components/skeletons/ChatSkeleton';
import { useUserProfile } from '@/context/UserProfileContext';
import { useUserPresence } from '@/hooks/use-userPresence';
import { IonContent, IonModal, IonPage } from '@ionic/react';
import ChatList from '../components/messages/ChatList';
import ChatView from '../components/messages/ChatView';
import NewChatView from '../components/messages/NewChatView';
import { useTranslation } from 'react-i18next';

interface MessagesParams {
  conversationId?: string;
}

export default function Messages() {
  const { t } = useTranslation(['MGP', 'Auth']);
  document.title = t('messages');

  const { profile } = useUserProfile();
  // Cast the profile to any to avoid TypeScript errors with the useUserPresence hook
  // This is safe because useUserPresence handles null values internally
  useUserPresence(profile as any);

  // Get conversationId from URL params
  const { conversationId } = useParams<MessagesParams>();
  const history = useHistory();
  const location = useLocation();

  // State management for chat views

  // Initialiser activeChat à partir de l'URL dès le début
  const [activeChat, setActiveChat] = useState<string | null>(conversationId || null);
  const [openNewChat, setOpenNewChat] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);

  // Fonction personnalisée pour mettre à jour activeChat et l'URL si nécessaire
  const updateActiveChat = useCallback((chatId: string | null) => {
    console.log('updateActiveChat called with:', chatId);

    // Mettre à jour l'état local
    setActiveChat(chatId);

    // Mettre à jour l'URL seulement si nécessaire
    if (chatId && location.pathname !== `/messages/c/${chatId}`) {
      console.log('Updating URL to:', `/messages/c/${chatId}`);
      history.push(`/messages/c/${chatId}`);
    } else if (!chatId && location.pathname !== '/messages') {
      console.log('Updating URL to: /messages');
      history.push('/messages');
    }
  }, [history, location.pathname]);

  // Wrapper pour setActiveChat qui respecte le type React.Dispatch<React.SetStateAction<string | null>>
  const handleSetActiveChat = useCallback((id: React.SetStateAction<string | null>) => {
    // Si id est une fonction, l'appliquer à activeChat pour obtenir la nouvelle valeur
    const newId = typeof id === 'function' ? id(activeChat) : id;
    updateActiveChat(newId);
  }, [activeChat, updateActiveChat]);

  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth <= 1024);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Mettre à jour activeChat quand l'URL change
  useEffect(() => {
    // Si l'URL contient un conversationId, mettre à jour activeChat
    console.log('URL changed, conversationId:', conversationId, 'activeChat:', activeChat);
    
    // Important: Always update the state when URL changes, regardless of current state
    if (conversationId) {
      setActiveChat(conversationId);
      setOpenNewChat(false);
    } else if (location.pathname === '/messages') {
      // Only reset activeChat if we're on the main messages route
      setActiveChat(null);
    }
  }, [conversationId, location.pathname]);

  if (!profile) {
    return (
      <IonPage>
        <IonContent>
          <ChatSkeleton />
        </IonContent>
      </IonPage>
    );
  }
  // return (
  //   <IonPage>
  //     <IonContent>
  //       <ChatSkeleton />
  //     </IonContent>
  //   </IonPage>
  // );

  return (
    <IonPage>
      <IonContent>
        <div className={`flex h-full md:pb-0 bg-secondary ${activeChat ? 'z-50' : 'z-10'}`}>
          {isMobileView ? (
            <div className="w-full">
              {activeChat ? (
                <ChatView
                  userId={profile.userId}
                  activeChat={activeChat}
                  goBack={() => {
                    handleSetActiveChat(null);
                  }}
                />
              ) : (
                <ChatList
                  userId={profile?.userId}
                  activeChat={activeChat}
                  setActiveChat={handleSetActiveChat}
                  openNewChat={() => setOpenNewChat(true)}
                />
              )}
            </div>
          ) : (
            <>
              <ChatList
                userId={profile.userId}
                activeChat={activeChat}
                setActiveChat={(id) => {
                  setOpenNewChat(false);
                  handleSetActiveChat(id);
                }}
                openNewChat={() => {
                  handleSetActiveChat(null);
                  setOpenNewChat(true);
                }}
              />
              {activeChat ? (
                <ChatView
                  userId={profile.userId}
                  activeChat={activeChat}
                  goBack={() => {
                    handleSetActiveChat(null);
                  }}
                />
              ) : (
                <div className="flex-1 flex items-center justify-center bg-card">
                  <div className="text-center">
                    <GolfIcon name="message-multiple" className="mx-auto size-20 opacity-70" />
                    <h3 className="mt-2 text-lg font-medium">{t('select_chat_placeholder')}</h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Choisissez une conversation dans la liste ou commencez une nouvelle discussion
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
          <IonModal
            isOpen={openNewChat}
            onDidDismiss={() => setOpenNewChat(false)}
            className="chat-modal"
          >
            <NewChatView
              userId={profile.userId}
              closeNewChat={() => setOpenNewChat(false)}
              setActiveChat={(id) => {
                handleSetActiveChat(id);
              }}
            />
          </IonModal>
        </div>
      </IonContent>
    </IonPage>
  );
}